/**
 * Initiative panel — replaces the legend in the right column during an
 * encounter. Shows tokens in initiative order, each with its dealt card,
 * the active token highlighted, already-acted tokens greyed.
 *
 * Each row stays draggable onto the canvas (reusing LEGEND_DRAG_MIME) and a
 * click recenters the camera on that token — same affordances as the legend.
 */

import { openTokenDeleteMenu } from './contextMenu.js';
import { activeIndex } from './encounter.js';
import { LEGEND_DRAG_MIME } from './legend.js';
import type { Card, EncounterState, Token } from './types.js';

const DEFAULT_NPC_IMAGE = '/default-npc.svg';

let onTokenClick: ((token: Token) => void) | null = null;

export function mountInitiativePanel(
  root: HTMLElement,
  tokenClickHandler?: (token: Token) => void,
): { render: (encounter: EncounterState, tokens: readonly Token[]) => void } {
  onTokenClick = tokenClickHandler ?? null;
  return {
    render(encounter, tokens) {
      renderPanel(root, encounter, tokens);
    },
  };
}

function renderPanel(root: HTMLElement, encounter: EncounterState, tokens: readonly Token[]): void {
  const byId = new Map(tokens.map((t) => [t.id, t]));
  const activeIdx = activeIndex(encounter);

  const header = document.createElement('div');
  header.className = 'initiative-header';
  header.textContent = `Round ${encounter.round}`;

  const list = document.createElement('ol');
  list.className = 'initiative-list';

  encounter.order.forEach((entry, idx) => {
    const token = byId.get(entry.tokenId);
    if (token === undefined) return; // reconcile should prevent this, but be safe

    const li = document.createElement('li');
    li.className = 'initiative-entry';
    if (activeIdx >= 0 && idx === activeIdx) li.classList.add('is-active');
    if (activeIdx >= 0 && idx < activeIdx) li.classList.add('has-acted');
    li.draggable = true;
    li.dataset.tokenId = String(token.id);

    li.addEventListener('dragstart', (e) => {
      if (e.dataTransfer === null) return;
      e.dataTransfer.setData(LEGEND_DRAG_MIME, String(token.id));
      e.dataTransfer.effectAllowed = 'move';
    });
    li.addEventListener('click', () => onTokenClick?.(token));
    li.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      openTokenDeleteMenu(token, e.clientX, e.clientY);
    });

    const cardEl = document.createElement('span');
    cardEl.className = 'initiative-card';
    applyCard(cardEl, entry.card);
    li.appendChild(cardEl);

    const img = document.createElement('img');
    img.className = 'initiative-portrait';
    img.alt = '';
    img.src = token.image_path !== null ? `/${token.image_path}` : DEFAULT_NPC_IMAGE;
    li.appendChild(img);

    const meta = document.createElement('div');
    meta.className = 'initiative-meta';
    const name = document.createElement('div');
    name.className = 'initiative-name';
    name.textContent = token.name;
    meta.appendChild(name);
    if (entry.pinned === true) {
      const pinned = document.createElement('div');
      pinned.className = 'initiative-pinned';
      pinned.textContent = '(joined this round)';
      meta.appendChild(pinned);
    }
    if (!token.is_placed) {
      const unplaced = document.createElement('div');
      unplaced.className = 'initiative-pinned';
      unplaced.textContent = '(off-map)';
      meta.appendChild(unplaced);
    }
    li.appendChild(meta);

    list.appendChild(li);
  });

  root.replaceChildren(header, list);
}

const SUIT_SYMBOL: Record<string, string> = {
  spade: '♠',
  heart: '♥',
  diamond: '♦',
  club: '♣',
};

const RANK_LABEL: Record<number, string> = {
  11: 'J',
  12: 'Q',
  13: 'K',
  14: 'A',
};

function applyCard(el: HTMLElement, card: Card): void {
  if (card.kind === 'joker') {
    el.textContent = card.color === 'red' ? '\u{1F0CF}R' : '\u{1F0CF}B';
    el.classList.add(card.color === 'red' ? 'card-red' : 'card-black');
    el.title = `${card.color} joker`;
    return;
  }
  const rank = RANK_LABEL[card.rank] ?? String(card.rank);
  el.textContent = `${rank}${SUIT_SYMBOL[card.suit] ?? '?'}`;
  // Hearts and diamonds render red; spades and clubs default (dark).
  el.classList.add(card.suit === 'heart' || card.suit === 'diamond' ? 'card-red' : 'card-black');
  el.title = `${rank} of ${card.suit}s`;
}
